import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCVStore } from '../store/cvStore'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { toast } from 'sonner'
import { Upload, FileText, Loader2, ArrowLeft, Sparkles } from 'lucide-react'
import { readFileAsText } from '../lib/fileReader'

export default function UploadPage() {
  const navigate = useNavigate()
  const { loading, parseCV, uploadCV, setCVData } = useCVStore()
  const [file, setFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<any>(null)
  const [cvName, setCvName] = useState('')
  const [cvText, setCvText] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const handleParse = async () => {
    if (file) {
      try {
        const text = await readFileAsText(file)
        const result = await parseCV(text)
        setParsedData(result)
        setCvName(file.name.replace(/\.[^/.]+$/, ''))
        toast.success('CV parsed successfully')
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to parse CV file'
        toast.error(msg)
      }
    } else if (cvText.trim().length >= 50) {
      try {
        const result = await parseCV(cvText)
        setParsedData(result)
        toast.success('CV parsed successfully')
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to parse CV text'
        toast.error(msg)
      }
    } else {
      toast.error('Upload a file or enter at least 50 characters of CV text')
    }
  }

  const handleSaveParsed = async () => {
    if (!parsedData) return
    try {
      setCVData(parsedData)
      await uploadCV(parsedData, undefined, cvName || 'Uploaded CV')
      navigate('/')
      toast.success('CV saved')
    } catch {
      toast.error('Failed to save CV')
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0])
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Upload CV</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="w-4 h-4 text-blue-600" />
            Upload a file
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              className="hidden"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2 text-blue-600">
                <FileText className="w-6 h-6" />
                <span className="font-medium">{file.name}</span>
                <span className="text-gray-400 text-sm">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <>
                <Upload className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-600 font-medium">Click or drop a file here</p>
                <p className="text-sm text-gray-400 mt-1">PDF, DOC, DOCX, or TXT</p>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            Or paste CV text
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs text-gray-500">CV Content</Label>
            <Textarea
              value={cvText}
              onChange={(e) => setCvText(e.target.value)}
              className="mt-1 min-h-[200px]"
              placeholder="Paste your CV text here to parse it with AI..."
            />
          </div>
        </CardContent>
      </Card>

      {!parsedData ? (
        <Button
          onClick={handleParse}
          disabled={loading || (!file && cvText.trim().length < 50)}
          className="w-full gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Parse with AI
        </Button>
      ) : (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 text-green-700">
              <Sparkles className="w-5 h-5" />
              <span className="font-medium">CV parsed successfully</span>
            </div>
            <div>
              <Label className="text-xs text-gray-500">CV Name</Label>
              <Input
                value={cvName}
                onChange={(e) => setCvName(e.target.value)}
                className="mt-1"
                placeholder="My CV"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveParsed} disabled={loading} className="gap-2 flex-1">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Save & Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => { setParsedData(null); setFile(null); setCvText('') }} className="gap-2">
                Start Over
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
